import { Box, Stack, Typography } from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';

import { QUESTIONS } from '@/benefits-flow/screener/questions';
import QuestionField from '@/benefits-flow/screener/QuestionField';
import Screen from '@/karehero-app/explore/Screen';
import {
  JOURNEY,
  PERSON,
  PRECISE,
  WAFFLE,
} from '@/karehero-app/explore/benefits-calculator/BenefitsCalculator.content';
import {
  Delta,
  Note,
  WorkingBanner,
} from '@/karehero-app/explore/benefits-calculator/Parts';

// Section F. The two questions nobody can answer from memory, and the escape
// hatch out of every async state.
//
// Worth noticing what the calculator actually asks for here: a band, not a
// figure. Under £16,000 / £16,000 to £23,250 / over £23,250. That tolerance is
// why extraction is a much lower bar here than on an Attendance Allowance form,
// and it is the strongest reason to think the calculator survives being fed by
// a voice note. Getting a band wrong changes a "might be worth pursuing", not a
// submitted claim.

const noop = () => undefined;

export const F1Band = () => (
  <Screen
    back
    footer={
      <Button ariaLabel={PRECISE.savings.unsure} variant='link' isFullWidth>
        {PRECISE.savings.unsure}
      </Button>
    }
  >
    <Stack spacing={2}>
      <QuestionField
        question={QUESTIONS['care-recipient-savings-held-in-bank-accounts']}
        recipientName={PERSON.name}
        value={undefined}
        onChange={noop}
      />
      <Note title='Why a band' body={PRECISE.savings.body} />
    </Stack>
  </Screen>
);
F1Band.displayName = 'F1Band';

// Leaving to go and look is a legitimate ending, not a drop-out. The record has
// to hold everything else so coming back is cheap.
export const F2Unsure = () => (
  <Screen
    title={PRECISE.unsure.title}
    subtitle={PRECISE.unsure.body}
    footer={
      <Button ariaLabel={PRECISE.unsure.cta} isFullWidth>
        {PRECISE.unsure.cta}
      </Button>
    }
  >
    <Note
      title='Saved'
      icon='check'
      body='Her nation, her age, her condition, the help she needs, and that she is single.'
    />
  </Screen>
);
F2Unsure.displayName = 'F2Unsure';

// The payoff frame for the whole exploration, and not the one it was expected to
// be. Three typed answers on top of the taps and the note, measured by the same
// engine: this is where nearly all the money is.
export const F3Worth = () => (
  <Screen title={PRECISE.worth.title} subtitle={PRECISE.worth.body}>
    <Delta
      before={{ ...JOURNEY.taps, ...JOURNEY.note }}
      after={{ ...JOURNEY.taps, ...JOURNEY.note, ...JOURNEY.typed }}
    />
  </Screen>
);
F3Worth.displayName = 'F3Worth';

// The escape hatch. No async state is allowed to be a dead end: if the queue is
// slow or the extraction fails, answering it yourself is always one tap away.
export const F4Escape = () => (
  <Screen
    title={PRECISE.escape.title}
    footer={
      <Button ariaLabel={PRECISE.escape.cta} isFullWidth>
        {PRECISE.escape.cta}
      </Button>
    }
  >
    <Stack spacing={2}>
      <Box>
        <WorkingBanner label={WAFFLE.meanwhile.banner} />
      </Box>
      <Typography variant='body2' color='text.secondary'>
        {PRECISE.escape.body}
      </Typography>
    </Stack>
  </Screen>
);
F4Escape.displayName = 'F4Escape';
